import { today, type CalendarDate } from "@internationalized/date";
import { PopoverTrigger } from "@radix-ui/react-popover";
import type { ReactNode } from "react";
import { useDateFormatter } from "react-aria";
import {
  Calendar as AriaCalendar,
  CalendarGrid as AriaCalendarGrid,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarCell,
  CalendarGridBody,
  CalendarHeaderCell,
  Heading,
  Button as RACButton,
  Text,
  type CalendarProps as AriaCalendarProps,
  type DateValue,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { TIMEZONE } from "@trip/constants";
import { cn } from "@trip/utils";

import { AngleLeftSmallIcon } from "~/components/icons/angle-left-small";
import { AngleRightSmallIcon } from "~/components/icons/angle-right-small";
import { CalendarIcon } from "~/components/icons/calendar-icon";
import { buttonVariants } from "~/components/ui/button";
import { Popover, PopoverContent } from "~/components/ui/popover";
import { focusRing } from "~/lib/aria";

export function CalendarGridHeader() {
  return (
    <AriaCalendarGridHeader>
      {(day) => (
        <CalendarHeaderCell className="text-xs font-medium text-neutral-500">
          {day}
        </CalendarHeaderCell>
      )}
    </AriaCalendarGridHeader>
  );
}

export interface CalendarProps<T extends DateValue>
  extends Omit<AriaCalendarProps<T>, "visibleDuration"> {
  errorMessage?: string;
}

const cell = tv({
  extend: focusRing,
  base: "w-full h-full flex items-center justify-center rounded-full forced-color-adjust-none text-neutral-900",
  variants: {
    selectionState: {
      none: "group-hover:bg-zinc-100 group-pressed:bg-zinc-200",
      middle: [
        "group-hover:bg-prune-200",
        "group-hover:group-invalid:bg-destructive/30 ",
      ],
      cap: "bg-prune-500 group-invalid:bg-destructive text-white",
    },
    isDisabled: {
      true: "text-zinc-300",
    },
  },
});

function CalendarGrid({ offset }: { offset: number }) {
  return (
    <AriaCalendarGrid offset={{ months: offset }} className="[&_td]:px-0">
      <CalendarGridHeader />
      <CalendarGridBody>
        {(date) => (
          <CalendarCell
            date={date}
            className="outside-month:text-zinc-300 data-selected:[&>span]:bg-prune-500 data-selected:[&>span]:text-white invalid:selected:bg-destructive/10 selection-start:rounded-s-full selection-end:rounded-e-full group size-9 cursor-default text-sm outline-0 [td:first-child_&]:rounded-s-full [td:last-child_&]:rounded-e-full"
          >
            {({
              formattedDate,
              isSelected,
              isSelectionStart,
              isSelectionEnd,
              isFocusVisible,
              isDisabled,
            }) => (
              <span
                className={cell({
                  selectionState:
                    isSelected && (isSelectionStart || isSelectionEnd)
                      ? "cap"
                      : isSelected
                        ? "middle"
                        : "none",
                  isDisabled,
                  isFocusVisible,
                })}
              >
                {formattedDate}
              </span>
            )}
          </CalendarCell>
        )}
      </CalendarGridBody>
    </AriaCalendarGrid>
  );
}

export function Calendar({
  errorMessage,
  ...props
}: Omit<CalendarProps<CalendarDate>, "visibleDuration" | "pageBehavior">) {
  const minValue =
    props.minValue === undefined
      ? today(TIMEZONE).add({ days: 1 })
      : props.minValue;
  return (
    <AriaCalendar
      {...props}
      visibleDuration={{ months: 2 }}
      pageBehavior="single"
      minValue={minValue}
    >
      <header className="flex w-full items-center gap-1 px-1 pb-4">
        <RACButton slot="previous">
          <AngleLeftSmallIcon aria-hidden />
        </RACButton>
        <Heading className="mx-2 flex-1 text-center font-medium text-neutral-900" />
        <RACButton slot="next">
          <AngleRightSmallIcon aria-hidden />
        </RACButton>
      </header>
      <div className="grid grid-cols-2 gap-4">
        <CalendarGrid offset={0} />
        <CalendarGrid offset={1} />
      </div>
      {errorMessage ? (
        <Text slot="errorMessage" className="text-destructive text-sm">
          {errorMessage}
        </Text>
      ) : null}
    </AriaCalendar>
  );
}

export function DatePicker({
  value,
  children,
  ...props
}: CalendarProps<CalendarDate> & { children?: ReactNode }) {
  const formatter = useDateFormatter({
    dateStyle: "medium",
  });
  return (
    <Popover>
      <PopoverTrigger asChild>
        {children ?? (
          <button
            data-slot="control"
            type="button"
            className={cn(
              buttonVariants(),
              "data-open:outline-2 outline-prune-500 w-full justify-start gap-2 px-3.5 font-normal",
            )}
          >
            <CalendarIcon className="" />
            {value ? formatter.format(value.toDate("UTC")) : "All dates"}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto rounded-xl shadow-2xl">
        <Calendar value={value} {...props} />
      </PopoverContent>
    </Popover>
  );
}
